import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import { apiUrl } from "../components/LoginSignup";

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/invoice-template.docx`;
const FALLBACK_TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/HAVE FAITH_ SITARAM ENTERPRISES (JAYANT SAHU)_INVC_00035_25_June_2026.docx`;

let templateCache = null;

const ONES = [
  "",
  "ONE",
  "TWO",
  "THREE",
  "FOUR",
  "FIVE",
  "SIX",
  "SEVEN",
  "EIGHT",
  "NINE",
  "TEN",
  "ELEVEN",
  "TWELVE",
  "THIRTEEN",
  "FOURTEEN",
  "FIFTEEN",
  "SIXTEEN",
  "SEVENTEEN",
  "EIGHTEEN",
  "NINETEEN",
];

const TENS = [
  "",
  "",
  "TWENTY",
  "THIRTY",
  "FORTY",
  "FIFTY",
  "SIXTY",
  "SEVENTY",
  "EIGHTY",
  "NINETY",
];

const formatBookingDate = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "N/A";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatAmount = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const convertHundreds = (num) => {
  let result = "";
  if (num > 99) {
    result += `${ONES[Math.floor(num / 100)]} HUNDRED`;
    num %= 100;
    if (num > 0) result += " ";
  }
  if (num > 19) {
    result += TENS[Math.floor(num / 10)];
    num %= 10;
    if (num > 0) result += ` ${ONES[num]}`;
  } else if (num > 0) {
    result += ONES[num];
  }
  return result.trim();
};

const amountToWords = (amount) => {
  const value = Math.round(Number(amount || 0));
  if (!value) return "ZERO ONLY";

  let words = "";
  let remaining = value;

  if (remaining >= 10000000) {
    words += `${convertHundreds(Math.floor(remaining / 10000000))} CRORE `;
    remaining %= 10000000;
  }
  if (remaining >= 100000) {
    words += `${convertHundreds(Math.floor(remaining / 100000))} LAKH `;
    remaining %= 100000;
  }
  if (remaining >= 1000) {
    words += `${convertHundreds(Math.floor(remaining / 1000))} THOUSAND `;
    remaining %= 1000;
  }
  if (remaining > 0) {
    words += convertHundreds(remaining);
  }

  return `${words.trim()} ONLY`;
};

const formatServices = (services) => {
  if (Array.isArray(services) && services.length > 0) {
    return services.filter(Boolean).join(", ");
  }
  if (services && typeof services === "string") {
    return services.trim() || "Consulting Services";
  }
  return "Consulting Services";
};

const formatInvoiceNumber = (invoiceNumber) =>
  String(invoiceNumber).padStart(5, "0");

export const getInvoiceData = (booking) => {
  const received =
    (Number(booking.term_1) || 0) +
    (Number(booking.term_2) || 0) +
    (Number(booking.term_3) || 0);

  const grossAmount = received > 0 ? received : Number(booking.total_amount || 0);
  const taxableAmount = Number((grossAmount / 1.18).toFixed(2));
  const gstAmount = Number((grossAmount - taxableAmount).toFixed(2));

  if (!booking.invoice_number) {
    throw new Error("Invoice number is not assigned to this booking.");
  }

  const invoiceNumber = formatInvoiceNumber(booking.invoice_number);

  const companyName = (booking.company_name || "N/A").toUpperCase();
  const contactPerson = booking.contact_person
    ? ` (${String(booking.contact_person).toUpperCase()})`
    : "";

  return {
    INVOICE_NO: ` ${invoiceNumber}`,
    DATE: formatBookingDate(booking.payment_date || booking.date),
    COMPANY_NAME: `${companyName}${contactPerson}`,
    ADDRESS: booking.address || booking.state || "N/A",
    GST_NO:
      booking.gst && booking.gst !== "N/A" ? String(booking.gst).trim() : "N/A",
    PHONE: booking.contact_no ? String(booking.contact_no) : "N/A",
    EMAIL: (booking.email || "N/A").toUpperCase(),
    SERVICE: formatServices(booking.services),
    UNIT_PRICE: formatAmount(taxableAmount),
    LINE_PRICE: formatAmount(taxableAmount),
    TAXABLE_AMOUNT: formatAmount(taxableAmount),
    GST_AMOUNT: formatAmount(gstAmount),
    GROSS_AMOUNT: formatAmount(grossAmount),
    AMOUNT_WORDS: amountToWords(grossAmount),
  };
};

const getFileName = (booking, extension = "pdf") => {
  const company = (booking.company_name || "invoice")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  return `${company || "invoice"}_invoice.${extension}`;
};

const loadStoredInvoiceNumber = async (booking) => {
  if (booking?.invoice_number) {
    return booking;
  }

  const userSession = JSON.parse(localStorage.getItem("userSession") || "{}");
  if (!userSession?.token || !booking?._id) {
    return booking;
  }

  const response = await fetch(`${apiUrl}/booking/invoice/number/${booking._id}`, {
    method: "GET",
    headers: {
      authorization: userSession.token,
    },
  });

  if (!response.ok) {
    return booking;
  }

  const data = await response.json();
  return { ...booking, invoice_number: data.invoice_number };
};

const loadTemplate = async () => {
  if (templateCache) return templateCache;

  let response = await fetch(TEMPLATE_URL);
  if (!response.ok) {
    response = await fetch(FALLBACK_TEMPLATE_URL);
  }
  if (!response.ok) {
    throw new Error(
      "Invoice template not found. Please ensure invoice-template.docx exists in the public folder."
    );
  }

  templateCache = await response.arrayBuffer();
  return templateCache;
};

const generateFilledDocx = async (booking) => {
  const templateBuffer = await loadTemplate();
  const zip = new PizZip(templateBuffer);
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
  });

  doc.setData(getInvoiceData(booking));
  doc.render();

  return doc.getZip().generate({
    type: "arraybuffer",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
};

export const downloadInvoiceDocx = async (booking) => {
  const bookingWithNumber = await loadStoredInvoiceNumber(booking);
  const buffer = await generateFilledDocx(bookingWithNumber);
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  saveAs(blob, getFileName(bookingWithNumber, "docx"));
};

export const downloadInvoicePdf = async (booking) => {
  const userSession = JSON.parse(localStorage.getItem("userSession") || "{}");

  if (!userSession?.token) {
    throw new Error("Please log in again to download the invoice.");
  }

  const response = await fetch(`${apiUrl}/booking/invoice/pdf`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      authorization: userSession.token,
    },
    body: JSON.stringify(booking),
  });

  const contentType = response.headers.get("content-type") || "";

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || "Failed to generate invoice PDF. Please try again."
    );
  }

  if (!contentType.includes("application/pdf")) {
    throw new Error("Server did not return a valid PDF file.");
  }

  const arrayBuffer = await response.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  const isPdf =
    bytes.length > 500 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46;

  if (!isPdf) {
    throw new Error(
      "Downloaded file is corrupted or not a PDF. Please try again."
    );
  }

  const blob = new Blob([bytes], { type: "application/pdf" });
  saveAs(blob, getFileName(booking, "pdf"));
};

export const downloadInvoice = async (booking, format = "pdf") => {
  if (format === "docx") {
    await downloadInvoiceDocx(booking);
    return;
  }
  await downloadInvoicePdf(booking);
};
